import { useCallback, useEffect, useRef, useState } from 'react';

import { QueryHookOptions, useApolloClient } from '@apollo/client';
import { useStoreContext } from 'features/store';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import {
  ILoyaltyUserOfferItemsQuery,
  LoyaltyOfferRedemptionType,
  LoyaltyPaymentMethod,
  LoyaltyPromotionType,
  LoyaltyServiceMode,
  LoyaltyUserOfferItemsDocument,
  useLoyaltyOfferItemsLazyQuery,
} from 'generated/graphql-gateway';
import { useLoyaltySystemwideOffersByIdsQuery } from 'generated/sanity-graphql';
import { useLocaleSmartBlockContent } from 'hooks/use-locale-smart-block-content/use-locale-smart-block-content';
import { groqQuery } from 'remote/api/groq';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { parseOffers } from 'state/global-state/models/loyalty/offers/offers.utils';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useNetworkContext } from 'state/network';
import { getServiceModeFromLS } from 'state/service-mode';
import { toError } from 'utils/errors';
import logger from 'utils/logger';

import {
  AppPersonalizedOffer,
  AppPersonalizedOfferGroup,
  EngineNonPersonalizedOfferListItem,
  EngineOfferListItem,
  EnginePersonalizedOffer,
  LoyaltyAppliedOffer,
  LoyaltyOffer,
  SanityId,
  SanityOfferGroup,
  SanityOfferListItemWithExtras,
  isEngineNonPersonalizedOfferListItem,
  isEngineOfferGroup,
  isEnginePersonalizedOffer,
  isEnginePersonalizedOfferGroup,
} from '../types';
import { dedupeByIdPreservingOrder, upsertSanityOfferLists } from '../utils';

import {
  LOYALTY_OFFER_GROUPS_BY_IDS_GROQ,
  SORTED_LOYALTY_OFFERS_GROQ,
} from './sortedLoyaltyOffersQuery';
import { IAppliedRewards, IEntriesIdsMap, IQueryLoyaltyUserOffersOptions } from './types';
import { useLoyaltyIncentivesEvaluation } from './use-loyalty-incentive-evaluation';
import { useLoyaltyUserId } from './use-loyalty-user';
import { usePersonalizedOffers } from './use-personalized-offers';
import {
  flattenEntriesToMap,
  normalizeCartEntriesForOfferEvaluation,
  parseEntry,
} from './utils/incentive-evaluation';
import { processPersonalizedOfferGroup } from './utils/personalized-offer-groups';
import {
  IPersonalizedData,
  getOfferGroupCmsIds,
  mergePersonalizedData,
} from './utils/personalized-offers';
import { isWalmartQuarterlyOfferGroup } from './utils/walmart-offer-group';

interface IUseLoyaltyOffersOptions {
  legacyOfferQueriesEnabled: boolean;
}

// How many times to retry fetching applied-offer engine/incentive data after a transient or
// superseded response before giving up for the current hydration target.
const APPLIED_OFFER_QUERY_MAX_ATTEMPTS = 3;
const APPLIED_OFFER_QUERY_RETRY_DELAY_MS = 3000;

const USER_OFFERS_QUERY_SCOPE = {
  AUTHORITATIVE: 'authoritative',
  SCOPED: 'scoped',
} as const;

type UserOffersQueryScope = typeof USER_OFFERS_QUERY_SCOPE[keyof typeof USER_OFFERS_QUERY_SCOPE];

interface IUserOffersRequestOwnership {
  generation: number;
  scope: UserOffersQueryScope;
  authoritativeGeneration: number;
}

interface IUserOffersProcessingContext extends IUserOffersRequestOwnership {
  isStale?: () => boolean;
}

interface IUserOffersProcessingResult {
  didCommit: boolean;
  resolvedIds: string[];
}

type AppliedOfferHydrationPhase =
  | 'idle'
  | 'waiting-for-authoritative'
  | 'requesting'
  | 'retry-wait'
  | 'settled'
  | 'failed';

interface IAppliedOfferHydrationState {
  key: string | null;
  phase: AppliedOfferHydrationPhase;
}

interface IAppliedOfferHydrationCoverage {
  loyaltyId: string | null;
  appliedIdsKey: string;
  resolvedIds: Set<string>;
}

type UserOffersQueryOptions = QueryHookOptions<any, any> & {
  isStale?: () => boolean;
  scope: UserOffersQueryScope;
};

const getReturnedEngineOfferItemIds = (offerItems: readonly EngineOfferListItem[]): string[] =>
  Array.from(
    new Set(
      offerItems.flatMap(item => {
        const childIds =
          isEngineOfferGroup(item) || isEnginePersonalizedOfferGroup(item)
            ? item.offers.flatMap(offer => (offer.id ? [offer.id] : []))
            : [];
        return item.id ? [item.id, ...childIds] : childIds;
      })
    )
  );

/** @deprecated useLoyaltyContext instead as this hook has side effects and should only be mounted once */
export const useLoyaltyOffers = ({ legacyOfferQueriesEnabled }: IUseLoyaltyOffersOptions) => {
  // This flag is parent to the next ones
  const loyaltyOffersEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_OFFERS));
  // This flag replaces Offers 3.0
  const loyaltyStandardOffersEnabled = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_STANDARD_OFFERS)
  );

  const enableLoyaltyStandardOffers = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_STANDARD_OFFERS);
  const offersCooldownEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_GLOBAL_OFFERS_COOLDOWN));
  const client = useApolloClient();
  const [evaluating, setEvaluating] = useState(false);
  const dispatch = useAppDispatch();
  const { loyaltyUserId } = useLoyaltyUserId();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const { store } = useStoreContext();
  const legacyOfferQueriesEnabledRef = useRef(legacyOfferQueriesEnabled);
  legacyOfferQueriesEnabledRef.current = legacyOfferQueriesEnabled;
  const sortedOffersRef = useRef<(LoyaltyOffer | AppPersonalizedOffer)[]>([]);
  const injectedWalmartQuarterlySourceGroupIdsRef = useRef<Set<string>>(new Set());
  const userOffersRequestGenerationsRef = useRef<Record<UserOffersQueryScope, number>>({
    [USER_OFFERS_QUERY_SCOPE.AUTHORITATIVE]: 0,
    [USER_OFFERS_QUERY_SCOPE.SCOPED]: 0,
  });
  const pendingAuthoritativeUserOffersRequestCountRef = useRef(0);
  const appliedOfferHydrationCoverageRef = useRef<IAppliedOfferHydrationCoverage>({
    loyaltyId: null,
    appliedIdsKey: '',
    resolvedIds: new Set(),
  });
  const hasFetchedCmsOffersRef = useRef(false);
  const isFetchingCmsOffersRef = useRef(false);
  const cartEntriesIdsMap = useRef<IEntriesIdsMap>({});
  const { language } = useLocale();

  const [
    fetchPersonalizedOfferData,
    { loading: personalizedDataLoading },
  ] = usePersonalizedOffers();
  const storeId = store?.number;

  const { transformSmartBlockContent } = useLocaleSmartBlockContent();
  const { sanityEndpoints } = useNetworkContext();
  const [cmsOffersLoading, setCmsOffersLoading] = useState(false);
  const [pendingUserOffersProcessingCount, setPendingUserOffersProcessingCount] = useState(0);
  const isUserOffersProcessing = pendingUserOffersProcessingCount > 0;

  const beginUserOffersRequest = useCallback(
    (scope: UserOffersQueryScope): IUserOffersRequestOwnership => {
      const generations = userOffersRequestGenerationsRef.current;
      const generation = generations[scope] + 1;
      generations[scope] = generation;
      return {
        generation,
        scope,
        authoritativeGeneration: generations[USER_OFFERS_QUERY_SCOPE.AUTHORITATIVE],
      };
    },
    []
  );

  const isUserOffersRequestCurrent = useCallback(
    ({ generation, scope, authoritativeGeneration }: IUserOffersRequestOwnership): boolean => {
      const generations = userOffersRequestGenerationsRef.current;
      if (generations[scope] !== generation) {
        return false;
      }
      if (scope === USER_OFFERS_QUERY_SCOPE.AUTHORITATIVE) {
        return true;
      }
      return (
        generations[USER_OFFERS_QUERY_SCOPE.AUTHORITATIVE] === authoritativeGeneration &&
        pendingAuthoritativeUserOffersRequestCountRef.current === 0
      );
    },
    []
  );

  const fetchLocalizedOfferGroups = useCallback(
    async (ids: string[]): Promise<SanityOfferGroup[] | null> => {
      if (!ids.length) {
        return [];
      }

      try {
        const data = await groqQuery<SanityOfferGroup[], { ids: string[]; language: string }>(
          sanityEndpoints.groq,
          LOYALTY_OFFER_GROUPS_BY_IDS_GROQ,
          { ids, language },
          { loggingTag: 'loyaltyOfferGroupsByIds' }
        );
        return Array.isArray(data) ? data : [];
      } catch (error) {
        logger.error({ message: 'groq fetch loyaltyOfferGroupsByIds error', error });
        return null;
      }
    },
    [language, sanityEndpoints.groq]
  );

  const queryFeatureSortedLoyaltyOffers = useCallback(async () => {
    if (!legacyOfferQueriesEnabledRef.current || isFetchingCmsOffersRef.current) {
      return;
    }
    isFetchingCmsOffersRef.current = true;
    setCmsOffersLoading(true);
    try {
      const data = await groqQuery<
        {
          autoEnabledSystemwideOffers?: SanityOfferListItemWithExtras[] | null;
          sortedSystemwideOffers?: SanityOfferListItemWithExtras[] | null;
        },
        { language: string }
      >(
        sanityEndpoints.groq,
        SORTED_LOYALTY_OFFERS_GROQ,
        { language },
        { loggingTag: 'sortedSystemwideOffers' }
      );

      if (!legacyOfferQueriesEnabledRef.current) {
        return;
      }

      const mergedSystemwideOffers = dedupeByIdPreservingOrder([
        ...(data?.sortedSystemwideOffers ?? []),
        ...(data?.autoEnabledSystemwideOffers ?? []),
      ]);

      if (mergedSystemwideOffers.length) {
        // A group returned by the primary sorted CMS query is no longer fallback-injected.
        // Preserve it as a normal list item if its quarterly classification later changes.
        mergedSystemwideOffers.forEach(item => {
          if (item._id) {
            injectedWalmartQuarterlySourceGroupIdsRef.current.delete(item._id);
          }
        });
        sortedOffersRef.current = upsertSanityOfferLists(
          sortedOffersRef.current,
          mergedSystemwideOffers as LoyaltyOffer[]
        );
      }
      hasFetchedCmsOffersRef.current = true;
    } catch (error) {
      logger.error({ message: 'groq fetch sortedSystemwideOffers error', error });
    } finally {
      isFetchingCmsOffersRef.current = false;
      setCmsOffersLoading(false);
    }
  }, [sanityEndpoints.groq, language]);

  const refetchCmsOffers = useCallback(async () => {
    if (!legacyOfferQueriesEnabled || !legacyOfferQueriesEnabledRef.current) {
      return;
    }
    await queryFeatureSortedLoyaltyOffers();
  }, [legacyOfferQueriesEnabled, queryFeatureSortedLoyaltyOffers]);

  const getPersonalizedOffer = useCallback(
    (engineOffer: EnginePersonalizedOffer, personalizedOfferData: IPersonalizedData) => {
      let personalizedOffer: AppPersonalizedOffer | null = null;

      try {
        personalizedOffer = mergePersonalizedData({
          engineOffer,
          transformSmartBlockContent,
          ...personalizedOfferData,
        });
      } catch (error) {
        const err = toError(error);
        const message = err.message ? `Error: ${err.message}` : '';
        logger.error(`Failed processing personalized offer: ${engineOffer.id} - ${message}`);
      }

      return personalizedOffer;
    },
    [transformSmartBlockContent]
  );

  const onUserOffersQueryComplete = useCallback(
    // Request generations are allocated per scope before network execution. Authoritative starts
    // invalidate older scoped work, while scoped starts cannot invalidate authoritative work.
    // `isStale` also lets a caller invalidate its own request (for example, effect cleanup). These
    // checks run before and after localized CMS work because staleness can develop during that
    // await. Authoritative responses replace state; id-scoped responses only upsert returned data.
    async (
      data: ILoyaltyUserOfferItemsQuery,
      { generation, scope, authoritativeGeneration, isStale }: IUserOffersProcessingContext
    ): Promise<IUserOffersProcessingResult> => {
      const ownership = { generation, scope, authoritativeGeneration };
      if (
        !legacyOfferQueriesEnabledRef.current ||
        isStale?.() ||
        !isUserOffersRequestCurrent(ownership)
      ) {
        return { didCommit: false, resolvedIds: [] };
      }

      const isCurrentProcessor = () =>
        legacyOfferQueriesEnabledRef.current &&
        !isStale?.() &&
        isUserOffersRequestCurrent(ownership);
      const isAuthoritative = scope === USER_OFFERS_QUERY_SCOPE.AUTHORITATIVE;

      const loyaltyUser = data?.loyaltyUserV2;
      const offerRedemptionAvailableAfter =
        loyaltyUser?.offerRedemptionAvailability?.availableAfter;

      const engineUserOfferItems = (loyaltyUser?.offerItems || []) as EngineOfferListItem[];

      setPendingUserOffersProcessingCount(count => count + 1);
      try {
        // Source Offer Group content must be fetched independently of the presentation flag so a
        // runtime flag activation can render immediately. GROQ preserves the active locale, unlike
        // the generated OfferGroup GraphQL fragment, whose aliases are fixed to English.
        const offerGroupCmsIds = getOfferGroupCmsIds(engineUserOfferItems, true);
        const [personalizedOffersResult, localizedOfferGroupsResult] = await Promise.all([
          fetchPersonalizedOfferData(engineUserOfferItems),
          fetchLocalizedOfferGroups(offerGroupCmsIds),
        ]);
        const localizedOfferGroups = localizedOfferGroupsResult ?? [];

        if (!isCurrentProcessor()) {
          return { didCommit: false, resolvedIds: [] };
        }
        if (personalizedOffersResult.status === 'failed') {
          return { didCommit: false, resolvedIds: [] };
        }
        const personalizedOfferData = personalizedOffersResult.data;

        const appPersonalizedOffers: AppPersonalizedOffer[] = [];
        const appPersonalizedOfferGroups: AppPersonalizedOfferGroup[] = [];
        const engineOfferListItems: EngineNonPersonalizedOfferListItem[] = [];
        const resolvedIds = new Set<string>();
        const localizedOfferGroupsMap = Object.fromEntries(
          localizedOfferGroups.map(group => [group._id, group])
        ) as Record<SanityId, SanityOfferGroup>;
        const offerGroupsMap = {
          ...(personalizedOfferData?.offerGroupsMap ?? {}),
          ...localizedOfferGroupsMap,
        };

        engineUserOfferItems.forEach(item => {
          if (isEnginePersonalizedOffer(item)) {
            if (personalizedOfferData) {
              const personalizedOffer = getPersonalizedOffer(item, personalizedOfferData);
              if (personalizedOffer) {
                appPersonalizedOffers.push(personalizedOffer);
                resolvedIds.add(item.id);
              }
            }
            return;
          }

          if (isEnginePersonalizedOfferGroup(item)) {
            if (personalizedOfferData) {
              const group = processPersonalizedOfferGroup(
                item,
                personalizedOfferData.systemwideOffersMap,
                offerGroupsMap
              );
              if (group) {
                appPersonalizedOfferGroups.push(group);
                resolvedIds.add(item.id);
                // A POG can materialize only a subset of its raw children. Coverage must follow
                // the app offers that were actually built, leaving missing applied children retryable.
                group.offers.forEach(offer => resolvedIds.add(offer.loyaltyEngineId));
              }
            }
            engineOfferListItems.push(item);
            return;
          }

          if (isEngineNonPersonalizedOfferListItem(item)) {
            engineOfferListItems.push(item);
          }
          getReturnedEngineOfferItemIds([item]).forEach(id => resolvedIds.add(id));
        });

        let injectedSourceGroupIdsToRemove: string[] = [];
        if (localizedOfferGroupsResult) {
          const previouslyInjectedSourceGroupIds =
            injectedWalmartQuarterlySourceGroupIdsRef.current;
          const explicitlyReclassifiedSourceGroupIds = new Set<string>(
            localizedOfferGroups
              .filter(
                group =>
                  previouslyInjectedSourceGroupIds.has(group._id) &&
                  !isWalmartQuarterlyOfferGroup(group)
              )
              .map(group => group._id)
          );
          // Only an authoritative response can infer removal from absence. A scoped response can
          // remove fallback-managed groups it explicitly returned with non-quarterly CMS data, but
          // must preserve managed IDs outside its requested scope.
          const sourceGroupIdsToRemove = isAuthoritative
            ? previouslyInjectedSourceGroupIds
            : explicitlyReclassifiedSourceGroupIds;
          injectedSourceGroupIdsToRemove = Array.from(sourceGroupIdsToRemove);
          const offersWithoutInjectedSourceGroups = sortedOffersRef.current.filter(
            offer => !sourceGroupIdsToRemove.has(offer._id)
          );
          const existingCmsOfferIds = new Set(
            offersWithoutInjectedSourceGroups.map(offer => offer._id)
          );
          const localizedGroupsAlreadyInList = localizedOfferGroups.filter(group =>
            existingCmsOfferIds.has(group._id)
          );
          const walmartQuarterlySourceGroupsToInject = localizedOfferGroups.filter(
            group => !existingCmsOfferIds.has(group._id) && isWalmartQuarterlyOfferGroup(group)
          );

          sortedOffersRef.current = upsertSanityOfferLists(offersWithoutInjectedSourceGroups, ([
            ...localizedGroupsAlreadyInList,
            ...walmartQuarterlySourceGroupsToInject,
          ] as unknown) as LoyaltyOffer[]);
          const injectedSourceGroupIds = walmartQuarterlySourceGroupsToInject.map(
            group => group._id
          );
          injectedWalmartQuarterlySourceGroupIdsRef.current = isAuthoritative
            ? new Set(injectedSourceGroupIds)
            : new Set([
                ...Array.from(previouslyInjectedSourceGroupIds).filter(
                  id => !explicitlyReclassifiedSourceGroupIds.has(id)
                ),
                ...injectedSourceGroupIds,
              ]);
        }

        sortedOffersRef.current = sortedOffersRef.current.concat(appPersonalizedOffers);

        if (offerRedemptionAvailableAfter && offersCooldownEnabled) {
          dispatch(actions.loyalty.setOfferRedemptionAvailableAfter(offerRedemptionAvailableAfter));
        }
        if (injectedSourceGroupIdsToRemove.length) {
          dispatch(
            actions.loyalty.reconcileCmsOffers({
              offers: sortedOffersRef.current,
              removeIds: injectedSourceGroupIdsToRemove,
            })
          );
        } else if (isAuthoritative || engineUserOfferItems.length) {
          dispatch(actions.loyalty.setCmsOffers(sortedOffersRef.current));
        }
        if (isAuthoritative) {
          dispatch(actions.loyalty.setEngineOfferListItems(engineOfferListItems));
          dispatch(actions.loyalty.setAppPersonalizedOffers(appPersonalizedOffers));
          dispatch(actions.loyalty.setAppPersonalizedOfferGroups(appPersonalizedOfferGroups));
        } else if (engineUserOfferItems.length) {
          dispatch(
            actions.loyalty.upsertUserOffers({
              engineOfferListItems,
              appPersonalizedOffers,
              appPersonalizedOfferGroups,
            })
          );
        }
        return { didCommit: true, resolvedIds: Array.from(resolvedIds) };
      } finally {
        setPendingUserOffersProcessingCount(count => Math.max(0, count - 1));
      }
    },
    [
      dispatch,
      fetchLocalizedOfferGroups,
      fetchPersonalizedOfferData,
      getPersonalizedOffer,
      offersCooldownEnabled,
      isUserOffersRequestCurrent,
    ]
  );

  const [queryEngineOffers, { loading: engineOffersLoading }] = useLoyaltyOfferItemsLazyQuery({
    fetchPolicy: 'network-only',
    variables: {
      where: {
        storeId,
        omitInvalids: false,
        includeOfferGroups: true,
      },
    },
    onCompleted: data => {
      if (legacyOfferQueriesEnabledRef.current && data?.loyaltyOfferItems?.length) {
        dispatch(actions.loyalty.setCmsOffers(sortedOffersRef.current));
        dispatch(
          actions.loyalty.setGuestEngineOfferListItems(
            data.loyaltyOfferItems as EngineOfferListItem[]
          )
        );
      }
    },
  });

  // Attach applied offers ids to get the full collection of offers from CMS
  const appliedLoyaltyOffersIds = appliedOffers.map(({ cmsId }) => cmsId || '');
  const appliedLoyaltyOfferEngineIds = Array.from(
    new Set(appliedOffers.flatMap(({ id }) => (id ? [id] : [])))
  ).sort();
  const appliedLoyaltyOfferEngineIdsKey = appliedLoyaltyOfferEngineIds.join(',');

  const { loading: swoffersLoading } = useLoyaltySystemwideOffersByIdsQuery({
    variables: {
      ids: appliedLoyaltyOffersIds,
    },
    skip: !legacyOfferQueriesEnabled || !appliedLoyaltyOffersIds.length,
    onCompleted(data) {
      if (legacyOfferQueriesEnabledRef.current && data?.allSystemwideOffers) {
        sortedOffersRef.current = sortedOffersRef.current.concat(
          data.allSystemwideOffers as LoyaltyOffer[]
        );

        dispatch(actions.loyalty.setCmsOffers(sortedOffersRef.current));
      }
    },
  });

  const [pendingUserOffersRequestCount, setPendingUserOffersRequestCount] = useState(0);
  const [
    pendingAuthoritativeUserOffersRequestCount,
    setPendingAuthoritativeUserOffersRequestCount,
  ] = useState(0);
  const [authoritativeUserOffersEpoch, setAuthoritativeUserOffersEpoch] = useState(0);
  const [
    appliedOfferHydrationCoverageRevision,
    setAppliedOfferHydrationCoverageRevision,
  ] = useState(0);
  const queryUserOffersLoading = pendingUserOffersRequestCount > 0;

  const recordAppliedOfferHydrationCoverage = useCallback(
    ({
      loyaltyId,
      appliedIdsKey,
      resolvedIds,
      replace,
      publishRevision = true,
    }: {
      loyaltyId: string;
      appliedIdsKey: string;
      resolvedIds: string[];
      replace: boolean;
      publishRevision?: boolean;
    }) => {
      const previousCoverage = appliedOfferHydrationCoverageRef.current;
      const existingIds =
        !replace &&
        previousCoverage.loyaltyId === loyaltyId &&
        previousCoverage.appliedIdsKey === appliedIdsKey
          ? previousCoverage.resolvedIds
          : new Set<string>();
      appliedOfferHydrationCoverageRef.current = {
        loyaltyId,
        appliedIdsKey,
        resolvedIds: new Set([...existingIds, ...resolvedIds]),
      };
      if (publishRevision) {
        setAppliedOfferHydrationCoverageRevision(revision => revision + 1);
      }
    },
    []
  );

  // Returns whether the response committed current-generation state, so callers that need to
  // retry a transient failure or superseded response (see the applied-offer coordinator below)
  // do not mistake a successful network response for completed hydration.
  const queryUserOffers = useCallback(
    async ({ variables, isStale, scope, ...options }: UserOffersQueryOptions): Promise<boolean> => {
      if (!legacyOfferQueriesEnabledRef.current) {
        return false;
      }

      const ownership = beginUserOffersRequest(scope);
      const isAuthoritative = scope === USER_OFFERS_QUERY_SCOPE.AUTHORITATIVE;
      if (isAuthoritative) {
        pendingAuthoritativeUserOffersRequestCountRef.current += 1;
        setPendingAuthoritativeUserOffersRequestCount(count => count + 1);
        setAuthoritativeUserOffersEpoch(ownership.generation);
      }
      const { where, ...otherVariables } = variables || {};
      setPendingUserOffersRequestCount(count => count + 1);

      try {
        const { data } = await client.query<ILoyaltyUserOfferItemsQuery>({
          ...options,
          fetchPolicy: 'no-cache',
          query: LoyaltyUserOfferItemsDocument,
          variables: {
            ...otherVariables,
            where: {
              ...where,
              includeOfferGroups: true,
            },
          },
        });

        if (legacyOfferQueriesEnabledRef.current && data && !isStale?.()) {
          // Awaited so this only resolves once the (possibly slower)
          // personalized-offer transformation/dispatch has actually
          // finished — callers use this to know when it's safe to stop
          // treating the fetch as "in flight" (e.g. the applied-offer
          // coordinator's pending/loading gate below). Returning early
          // here let `onUserOffersQueryComplete`'s own async work outlive
          // the caller's notion of "done," which could let `OrderProvider`
          // treat the offer as settled before its CMS data actually landed.
          const processingResult = await onUserOffersQueryComplete(data, {
            ...ownership,
            isStale,
          });
          if (processingResult.didCommit && !isStale?.() && isUserOffersRequestCurrent(ownership)) {
            const requestLoyaltyId = variables?.loyaltyId;
            if (requestLoyaltyId) {
              const requestedIds = ((where?.ids || []) as Array<
                string | null | undefined
              >).filter((id): id is string => Boolean(id));
              const returnedIds = getReturnedEngineOfferItemIds(
                (data.loyaltyUserV2?.offerItems || []) as EngineOfferListItem[]
              );
              const materializedIds = new Set(processingResult.resolvedIds);
              const unresolvedReturnedIds = new Set(
                returnedIds.filter(id => !materializedIds.has(id))
              );
              const resolvedIds = isAuthoritative
                ? processingResult.resolvedIds
                : requestedIds.filter(id => !unresolvedReturnedIds.has(id));
              const didResolveScopedRequest =
                isAuthoritative || resolvedIds.length === requestedIds.length;
              // Retain partial scoped progress for the next attempt, but publish a revision only
              // at a terminal result. Publishing here would tear down this effect-owned retry
              // chain and reset its bounded attempt counter.
              recordAppliedOfferHydrationCoverage({
                loyaltyId: requestLoyaltyId,
                appliedIdsKey: isAuthoritative
                  ? appliedLoyaltyOfferEngineIdsKey
                  : requestedIds.slice().sort().join(','),
                resolvedIds,
                replace: isAuthoritative,
                publishRevision: didResolveScopedRequest,
              });
              return didResolveScopedRequest;
            }
          }
          return processingResult.didCommit;
        }
        return false;
      } catch (e) {
        logger.error(`Error fetching user offers: ${e}`);
        return false;
      } finally {
        if (isAuthoritative) {
          pendingAuthoritativeUserOffersRequestCountRef.current = Math.max(
            0,
            pendingAuthoritativeUserOffersRequestCountRef.current - 1
          );
          setPendingAuthoritativeUserOffersRequestCount(count => Math.max(0, count - 1));
        }
        setPendingUserOffersRequestCount(count => Math.max(0, count - 1));
      }
    },
    [
      beginUserOffersRequest,
      client,
      isUserOffersRequestCurrent,
      onUserOffersQueryComplete,
      appliedLoyaltyOfferEngineIdsKey,
      recordAppliedOfferHydrationCoverage,
    ]
  );

  // A single coordinator owns automatic engine hydration for every applied offer. Its target key
  // includes the authoritative generation, so a full refresh that invalidates scoped work causes
  // an actual replacement request rather than merely assigning new ownership to an old response.
  const appliedOfferHydrationCoverage = appliedOfferHydrationCoverageRef.current;
  const appliedOfferHydrationEnabled = Boolean(
    legacyOfferQueriesEnabled && loyaltyUserId && appliedLoyaltyOfferEngineIds.length
  );
  const areAppliedOffersResolved = Boolean(
    appliedOfferHydrationEnabled &&
      appliedOfferHydrationCoverage.loyaltyId === loyaltyUserId &&
      appliedOfferHydrationCoverage.appliedIdsKey === appliedLoyaltyOfferEngineIdsKey &&
      appliedLoyaltyOfferEngineIds.every(id => appliedOfferHydrationCoverage.resolvedIds.has(id))
  );
  const appliedOfferHydrationKey = appliedOfferHydrationEnabled
    ? `${loyaltyUserId}:${appliedLoyaltyOfferEngineIdsKey}:${authoritativeUserOffersEpoch}`
    : null;
  const [appliedOfferHydrationState, setAppliedOfferHydrationState] = useState<
    IAppliedOfferHydrationState
  >({ key: null, phase: 'idle' });
  const needsAppliedOfferHydration = appliedOfferHydrationEnabled && !areAppliedOffersResolved;
  const isAppliedOfferHydrationPending = Boolean(
    needsAppliedOfferHydration &&
      (appliedOfferHydrationState.key !== appliedOfferHydrationKey ||
        appliedOfferHydrationState.phase === 'waiting-for-authoritative' ||
        appliedOfferHydrationState.phase === 'requesting' ||
        appliedOfferHydrationState.phase === 'retry-wait')
  );

  useEffect(() => {
    if (!appliedOfferHydrationKey || !needsAppliedOfferHydration || !loyaltyUserId) {
      if (!appliedOfferHydrationKey) {
        appliedOfferHydrationCoverageRef.current = {
          loyaltyId: null,
          appliedIdsKey: '',
          resolvedIds: new Set(),
        };
      }
      setAppliedOfferHydrationState(current => {
        const nextState: IAppliedOfferHydrationState = {
          key: appliedOfferHydrationKey,
          phase: appliedOfferHydrationKey ? 'settled' : 'idle',
        };
        return current.key === nextState.key && current.phase === nextState.phase
          ? current
          : nextState;
      });
      return undefined;
    }

    let cancelled = false;
    let retryTimeoutId: ReturnType<typeof setTimeout> | undefined;

    if (pendingAuthoritativeUserOffersRequestCountRef.current > 0) {
      setAppliedOfferHydrationState({
        key: appliedOfferHydrationKey,
        phase: 'waiting-for-authoritative',
      });
      return () => {
        cancelled = true;
      };
    }

    const attemptFetch = (attempt: number) => {
      if (cancelled) {
        return;
      }
      if (pendingAuthoritativeUserOffersRequestCountRef.current > 0) {
        setAppliedOfferHydrationState({
          key: appliedOfferHydrationKey,
          phase: 'waiting-for-authoritative',
        });
        return;
      }

      setAppliedOfferHydrationState({ key: appliedOfferHydrationKey, phase: 'requesting' });
      queryUserOffers({
        scope: USER_OFFERS_QUERY_SCOPE.SCOPED,
        variables: {
          loyaltyId: loyaltyUserId,
          where: {
            omitInvalids: false,
            ids: appliedLoyaltyOfferEngineIdsKey.split(','),
          },
        },
        isStale: () => cancelled,
      }).then(success => {
        if (cancelled) {
          return;
        }
        if (success) {
          setAppliedOfferHydrationState({ key: appliedOfferHydrationKey, phase: 'settled' });
          return;
        }
        if (attempt >= APPLIED_OFFER_QUERY_MAX_ATTEMPTS) {
          setAppliedOfferHydrationState({ key: appliedOfferHydrationKey, phase: 'failed' });
          return;
        }
        setAppliedOfferHydrationState({ key: appliedOfferHydrationKey, phase: 'retry-wait' });
        retryTimeoutId = setTimeout(
          () => attemptFetch(attempt + 1),
          APPLIED_OFFER_QUERY_RETRY_DELAY_MS
        );
      });
    };

    attemptFetch(1);

    return () => {
      cancelled = true;
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
      }
    };
  }, [
    appliedLoyaltyOfferEngineIdsKey,
    appliedOfferHydrationCoverageRevision,
    appliedOfferHydrationKey,
    loyaltyUserId,
    needsAppliedOfferHydration,
    pendingAuthoritativeUserOffersRequestCount,
    queryUserOffers,
  ]);

  const queryLoyaltyUserOffers = useCallback(
    ({
      redemptionTypes,
      omitInvalids = false,
    }: {
      redemptionTypes: LoyaltyOfferRedemptionType[];
      omitInvalids?: boolean;
    }) => ({
      loyaltyId,
      cartEntries,
      appliedRewards,
      appliedLoyaltyOffers = [],
    }: IQueryLoyaltyUserOffersOptions) => {
      if (!legacyOfferQueriesEnabled || !legacyOfferQueriesEnabledRef.current) {
        return;
      }

      const normalizedCartEntries = normalizeCartEntriesForOfferEvaluation(
        cartEntries,
        appliedLoyaltyOffers
      );

      const parsedEntries = normalizedCartEntries?.reduce(
        parseEntry({
          type: LoyaltyPromotionType.OFFER,
          incentivesToExclude: appliedRewards,
        }),
        []
      );

      cartEntriesIdsMap.current = (parsedEntries || []).reduce(flattenEntriesToMap, {});

      return queryUserOffers({
        scope: USER_OFFERS_QUERY_SCOPE.AUTHORITATIVE,
        variables: {
          loyaltyId,
          where: {
            appliedIncentives: parseOffers(appliedLoyaltyOffers),
            cartEntries: parsedEntries,
            serviceMode: getServiceModeFromLS() || undefined,
            redemptionTypes,
            storeId,
            omitInvalids,
          },
        },
      });
    },
    [legacyOfferQueriesEnabled, queryUserOffers, storeId]
  );

  // Offers without loyaltyId should always be of redemption type STANDARD
  const queryLoyaltyOffers = useCallback(() => {
    if (!legacyOfferQueriesEnabled || !legacyOfferQueriesEnabledRef.current) {
      return;
    }

    const sm = getServiceModeFromLS();
    const lsm = Object.entries(LoyaltyServiceMode).find(([key]) => key === sm)?.[1] || undefined;
    queryEngineOffers({
      variables: {
        where: {
          serviceMode: lsm,
          storeId,
          redemptionTypes: [LoyaltyOfferRedemptionType.STANDARD],
          includeOfferGroups: true,
        },
      },
    });
  }, [legacyOfferQueriesEnabled, queryEngineOffers, storeId]);

  /**
   * Lazy-load sortedSystemwideOffers (CMS only). Call when a screen that needs offers mounts
   * (e.g. Loyalty Offers, Cart). Idempotent after first successful fetch — does not re-trigger
   * engine offer queries; screens run their own fetch flow (e.g. Offers uses refreshOffers/fetchOffers).
   */
  const ensureSortedOffersLoaded = useCallback(async () => {
    const shouldSkipLoad =
      !legacyOfferQueriesEnabled ||
      !enableLoyaltyStandardOffers ||
      hasFetchedCmsOffersRef.current ||
      isFetchingCmsOffersRef.current;
    if (shouldSkipLoad) {
      return;
    }
    await queryFeatureSortedLoyaltyOffers();
    if (!legacyOfferQueriesEnabledRef.current) {
      return;
    }
    queryLoyaltyOffers();
  }, [
    enableLoyaltyStandardOffers,
    legacyOfferQueriesEnabled,
    queryFeatureSortedLoyaltyOffers,
    queryLoyaltyOffers,
  ]);

  const queryLoyaltyUserStandardOffers = useCallback(
    (options: IQueryLoyaltyUserOffersOptions) =>
      queryLoyaltyUserOffers({
        redemptionTypes: [LoyaltyOfferRedemptionType.STANDARD],
      })(options),
    [queryLoyaltyUserOffers]
  );

  const { evaluateLoyaltyIncentives } = useLoyaltyIncentivesEvaluation();

  /**
   * Evaluates applied loyalty offers against cart entries.
   *
   * IMPORTANT: Callers must ensure the unified menu is loaded before calling
   * this function. Use `indexedMenu.isLoaded()` from `useUnifiedMenuContext()`
   * to check. This avoids stale closure issues when the check is done inside
   * this callback.
   *
   * @see YourCart - guards with `isUnifiedMenuLoaded` in useEffect
   * @see useCartAvailabilityCheck - guards with `isUnifiedMenuLoaded` in
   * useEffect
   */
  const evaluateLoyaltyUserOffers = useCallback(
    async (
      loyaltyId: string,
      appliedLoyaltyOffers: LoyaltyAppliedOffer[],
      cartEntries?: ILegacyCartEntry[],
      appliedLoyaltyRewards?: IAppliedRewards | null,
      subtotalAmount?: number,
      paymentMethod?: LoyaltyPaymentMethod | null
    ) => {
      const normalizedCartEntries = normalizeCartEntriesForOfferEvaluation(
        cartEntries,
        appliedLoyaltyOffers
      );

      const parsedEntries = normalizedCartEntries?.reduce(
        parseEntry({
          type: LoyaltyPromotionType.OFFER,
          incentivesToExclude: appliedLoyaltyRewards,
        }),
        []
      );

      cartEntriesIdsMap.current = (parsedEntries || []).reduce(flattenEntriesToMap, {});

      setEvaluating(true);

      try {
        const evaluationResult = await evaluateLoyaltyIncentives({
          loyaltyId,
          appliedIncentives: appliedLoyaltyOffers,
          incentiveType: LoyaltyPromotionType.OFFER,
          cartEntries: parsedEntries,
          subtotalAmount,
          paymentMethod,
          serviceMode: getServiceModeFromLS(),
          storeId,
        });

        if (evaluationResult) {
          dispatch(actions.loyalty.setOffersFeedbackMap(evaluationResult));
        }
      } finally {
        setEvaluating(false);
      }
    },
    [dispatch, evaluateLoyaltyIncentives, storeId]
  );

  useEffect(() => {
    dispatch(
      actions.loyalty.setOffersLoading(
        queryUserOffersLoading ||
          engineOffersLoading ||
          personalizedDataLoading ||
          cmsOffersLoading ||
          swoffersLoading ||
          evaluating ||
          isAppliedOfferHydrationPending ||
          isUserOffersProcessing
      )
    );
  }, [
    dispatch,
    queryUserOffersLoading,
    engineOffersLoading,
    personalizedDataLoading,
    cmsOffersLoading,
    isAppliedOfferHydrationPending,
    swoffersLoading,
    evaluating,
    isUserOffersProcessing,
  ]);

  return {
    evaluateLoyaltyUserOffers,
    ensureSortedOffersLoaded,
    loyaltyOffersEnabled,
    loyaltyStandardOffersEnabled,
    queryLoyaltyOffers,
    queryLoyaltyUserStandardOffers,
    refetchCmsOffers,
    cmsOffersLoading,
  };
};
